import { isPlatformServer } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '@curbnturf/environment';
import { Platform } from '@ionic/angular/standalone';
import { Subject } from 'rxjs';
import { Capacitor } from '@capacitor/core';

/**
 * Local fork of @curbnturf/network's PlatformService, needed by
 * CognitoService for iOS/native branching. Only change from the real class:
 * PROGRAM (@curbnturf/entities) -> environment.program directly.
 */
@Injectable({
  providedIn: 'root',
})
export class PlatformService {
  public resize: Subject<void>;

  private readonly platform = inject(Platform);
  private readonly platformId = inject<string>(PLATFORM_ID);

  constructor() {
    this.resize = this.platform?.resize;
  }

  isPhysicalDevice() {
    if (isPlatformServer(this.platformId)) {
      return false;
    }

    return Capacitor.isNativePlatform();
  }

  isIOS() {
    if (isPlatformServer(this.platformId)) {
      return false;
    }

    return Capacitor.getPlatform() === 'ios';
  }

  isAndroid() {
    if (isPlatformServer(this.platformId)) {
      return false;
    }

    return Capacitor.getPlatform() === 'android';
  }

  isIOSWeb() {
    if (isPlatformServer(this.platformId)) {
      return false;
    }

    return this.platform.is('ios') && this.platform.is('mobileweb');
  }

  isAndroidWeb() {
    if (isPlatformServer(this.platformId)) {
      return false;
    }

    return this.platform.is('android') && this.platform.is('mobileweb');
  }

  isMobileWeb() {
    return this.isIOSWeb() || this.isAndroidWeb();
  }

  isAppContext() {
    return environment.program === 'app';
  }

  isPhoneSize() {
    return (this.platform.isPortrait() && this.width() < 550) || (this.platform.isLandscape() && this.width() < 820);
  }

  isTabletSize() {
    return (this.platform.isPortrait() && this.width() >= 550) || (this.platform.isLandscape() && this.width() >= 820);
  }

  height() {
    return this.platform.height();
  }

  width() {
    return this.platform.width();
  }
}
